import type { Messages, PluralInfo } from "./types";

export default class Translator {
  public messages: Messages;
  public pluralForm: string;
  public getPlural: (n: number) => PluralInfo;
  constructor(messages: Messages, pluralForm?: string | null) {
    this.messages = messages;
    // default english plural form
    this.pluralForm = pluralForm || "nplurals=2; plural=(n != 1);";
    this.getPlural = _makePluralFunc(this.pluralForm);
  }

  private strFormat(msg: string, substitutes?: Record<string, any>) {
    if (!substitutes || Object.keys(substitutes).length === 0) {
      return msg;
    }
    let msgout = msg;
    for (const [key, value] of Object.entries(substitutes)) {
      msgout = msgout.replace(`{${key}}`, value);
    }
    return msgout;
  }

  private translateInner(
    messages: string | string[],
    n: number | null,
    substitutes?: Record<string, any>
  ): string {
    const msgArray = Array.isArray(messages) ? messages : [messages];
    if (n === null) {
      return this.strFormat(msgArray[0], substitutes);
    }
    const plural = this.getPlural(n);
    if (
      plural.plural === undefined ||
      plural.plural > plural.nplurals ||
      msgArray.length <= plural.plural
    ) {
      plural.plural = 0;
    }
    return this.strFormat(msgArray[plural.plural], substitutes);
  }

  public t(msgId: string, substitutes?: Record<string, any>): string {
    const data = this.messages[msgId];
    if (data === undefined) {
      console.warn("Translation missing:", msgId);
      return msgId;
    }
    return this.translateInner(data, null, substitutes);
  }

  public tn(
    msgId: string,
    n: number,
    substitutes?: Record<string, any>
  ): string {
    const data = this.messages[msgId];
    if (data === undefined) {
      console.warn("Translation missing:", msgId);
      return msgId;
    }
    return this.translateInner(data, n, substitutes);
  }

  public tx(
    messages: Messages,
    msgId: string,
    substitutes?: Record<string, any>
  ): string {
    const data = messages[msgId];
    if (data === undefined) {
      console.warn("Translation missing:", msgId);
      return msgId;
    }
    return this.translateInner(data, null, substitutes);
  }
}

function _makePluralFunc(pluralForm: string): (n: number) => PluralInfo {
  // Borrowed from https://github.com/guillaumepotier/gettext.js/blob/master/lib/gettext.js
  // Convert gettext plural form equation to a js function
  // List of plural forms for all languages:
  // http://docs.translatehouse.org/projects/localization-guide/en/latest/l10n/pluralforms.html
  const pf_re = new RegExp(
    "^\\s*nplurals\\s*=\\s*[0-9]+\\s*;\\s*plural\\s*=\\s*(?:\\s|[-\\?\\|&=!<>+*/%:;n0-9_()])+"
  );

  if (!pf_re.test(pluralForm))
    throw new Error(`The plural form is not valid: ${pluralForm}`);
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return new Function(
    "n",
    "var plural, nplurals; " +
      pluralForm +
      " return { nplurals: nplurals, plural: (plural === true ? 1 : (plural ? plural : 0)) };"
  );
}
